import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { 
  MicrophoneIcon, 
  StopIcon, 
  PlayIcon, 
  PauseIcon,
  ArrowUpTrayIcon,
  ChatBubbleLeftRightIcon,
  PlusIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  CheckCircleIcon
} from '@heroicons/react/24/solid';
import { useAudioRecorder } from '../hooks/useAudioRecorder';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { fn } from '../api/functions';
import { storage, db } from '../config/firebase';
import { collection, doc, setDoc, serverTimestamp, query, orderBy, getDocs, updateDoc, runTransaction } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import { useAuth } from '../contexts/AuthContext';
import { AnswerThread } from './AnswerThread';
import PhotoUpload from './PhotoUpload';

export const AudioRecorder = ({ 
  storyId, 
  questionId, 
  question, 
  parentAnswerId = null, 
  isFollowup = false, 
  existingAnswers = [], 
  onAnswerComplete, 
  onStartFollowup, 
  useSteppedInterface = false,
  // NEW: Collaborative story props
  isCollaborative = false,
  authorId = null,
  authorEmail = null,
  // Access control
  userAccessLevel = 'owner',
  // Demo mode
  demoMode = false,
  demoSessionId = null,
  // Story object for demo limits
  story = null
}) => {
  const { currentUser } = useAuth();

  const {
    isRecording,
    isPaused,
    audioBlob,
    duration,
    error,
    wakeLockSupported,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    resetRecording
  } = useAudioRecorder();

  // Production: Minimal logging
  // console.log('AudioRecorder - duration:', duration, 'isRecording:', isRecording);

  const [audioUrl, setAudioUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcription, setTranscription] = useState('');
  const [cleanedTranscription, setCleanedTranscription] = useState('');
  const [showThread, setShowThread] = useState(false);
  const [followupQuestion, setFollowupQuestion] = useState('');
  const [isGeneratingFollowup, setIsGeneratingFollowup] = useState(false);
  const [isRecordingFollowup, setIsRecordingFollowup] = useState(false);
  const [isCleaning, setIsCleaning] = useState(false);
  const [finalAnswer, setFinalAnswer] = useState('');
  const [justCompletedAnswer, setJustCompletedAnswer] = useState(false); // Track if we just completed an answer
  const [threadRefreshTrigger, setThreadRefreshTrigger] = useState(0); // Trigger AnswerThread refresh
  const [hasUploadError, setHasUploadError] = useState(false); // Track upload errors to prevent retrigger
  const [hasConvertedFollowup, setHasConvertedFollowup] = useState(false); // Track if user has converted a follow-up to story question
  const [photos, setPhotos] = useState([]);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [showStepTwo, setShowStepTwo] = useState(false); // Track stepped interface Step 2
  const [hasClickedContinue, setHasClickedContinue] = useState(false); // Track when Continue is clicked
  const audioRef = React.useRef(null);
  const isProcessingUpload = React.useRef(false); // Prevent duplicate upload attempts
  const recordingQuestionIdRef = React.useRef(null); // Question the in-memory recording belongs to
  const processedBlobRef = React.useRef(null); // Recording already submitted for transcription

  // Step management for stepped interface
  const getCurrentStep = () => {
    if (!useSteppedInterface) return null;
    
    // Step 1: Ready to answer (initial state)
    if (!audioBlob && !isRecording && !transcription && !showStepTwo) return 1;
    
    // Step 2: Ready to record (after clicking "I'm Ready to Record")
    if (showStepTwo && !isRecording && !audioBlob && !transcription) return 2;
    
    // Step 3: Recording in progress
    if (isRecording) return 3;
    
    // Step 4: Processing (uploading or transcribing)
    if ((audioBlob && (isUploading || isTranscribing)) || (audioBlob && !transcription)) return 4;
    
    // Step 5: Final state (merged review and complete) - shows when transcription is ready
    if (transcription) return 5;
    
    return 1; // default
  };

  // Load existing answers when they change
  useEffect(() => {
    // console.log('🔄 AudioRecorder: existingAnswers changed:', existingAnswers, 'justCompletedAnswer:', justCompletedAnswer);
    
    if (existingAnswers && existingAnswers.length > 0) {
      // Display the most recent answer's transcriptions
      const latestAnswer = existingAnswers[existingAnswers.length - 1];
      // console.log('📖 AudioRecorder: Loading latest answer:', latestAnswer);
      
      // Always load from database - it's the source of truth
      if (latestAnswer.rawTranscription) {
        setTranscription(latestAnswer.rawTranscription);
      } else {
        setTranscription('');
      }
      
      if (latestAnswer.cleanedTranscription) {
        setCleanedTranscription(latestAnswer.cleanedTranscription);
      } else {
        setCleanedTranscription('');
      }
      
      // Set final answer based on what's in the database
      // Prefer cleaned transcription if it exists, otherwise use raw
      const finalFromDB = latestAnswer.cleanedTranscription || latestAnswer.rawTranscription;
      setFinalAnswer(finalFromDB || '');
      
      // Load existing photos if they exist
      if (latestAnswer.photos && latestAnswer.photos.length > 0) {
        setPhotos(latestAnswer.photos);
      } else if (latestAnswer.photos === undefined) {
        // If photos property doesn't exist yet, don't clear existing photos
      } else {
        // Only clear if explicitly set to empty array
        setPhotos([]);
      }
      
      setShowThread(true);
      setJustCompletedAnswer(false); // Reset the flag once we have data from DB
      // console.log('✅ Set transcription from DB:', finalFromDB);
    } else if (!justCompletedAnswer) {
      // Only clear states if we didn't just complete an answer
      // This prevents clearing transcription due to Firestore read delay
      // console.log('🧹 AudioRecorder: No existing answers and no recent completion, clearing all states');
      setTranscription('');
      setCleanedTranscription('');
      setFinalAnswer('');
      setFollowupQuestion('');
      setIsRecordingFollowup(false);
      setShowThread(false);
      setHasConvertedFollowup(false);
    } else {
      // console.log('⏳ AudioRecorder: No existing answers but just completed - keeping transcription visible');
    }
  }, [existingAnswers, justCompletedAnswer]);

  // Clear local states when question changes
  useEffect(() => {
    // Reset follow-up related states when switching questions
    setFollowupQuestion('');
    setIsRecordingFollowup(false);
    setIsGeneratingFollowup(false);
    setJustCompletedAnswer(false); // Reset completion flag
    setHasConvertedFollowup(false); // Reset conversion flag when switching questions
    
    // Note: We don't reset photos here because they should persist when switching questions
    // Photos are managed by the existingAnswers useEffect
    
    // Reset loading states to prevent them from persisting
    setIsUploading(false);
    setIsTranscribing(false);
    setIsCleaning(false);
    
    // Reset audio playback states
    setIsPlaying(false);
    setAudioUrl(null);
    
    // Reset photos - they will be reloaded by the existingAnswers useEffect
    setPhotos([]);

    // Drop any recording still held in memory from the previous question, and
    // forget which question it belonged to. This reset alone cannot prevent the
    // cross-question upload -- resetRecording only queues a state update, so the
    // stale recording is still visible to the upload effect on this same render.
    // That effect therefore checks recordingQuestionIdRef before submitting.
    recordingQuestionIdRef.current = null;
    processedBlobRef.current = null;
    isProcessingUpload.current = false;
    resetRecording();
  }, [questionId]);

  // Cleanup audio URL on unmount
  useEffect(() => {
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  // Create audio URL when blob is available
  useEffect(() => {
    if (audioBlob) {
      const url = URL.createObjectURL(audioBlob);
      setAudioUrl(url);
      return () => {
        URL.revokeObjectURL(url);
      };
    }
  }, [audioBlob]);

  // Auto-upload and transcribe when recording finishes
  useEffect(() => {
    if (!audioBlob) return;

    // This exact recording has already been submitted. Flags like transcription
    // and hasUploadError get reset by the effects above, so they cannot be
    // trusted to mark a recording as handled -- the blob's own identity can.
    if (processedBlobRef.current === audioBlob) return;

    // The recording belongs to a different question (the user navigated away
    // after recording). Uploading it now would file the previous question's
    // audio as this question's answer, so discard it instead.
    if (recordingQuestionIdRef.current !== questionId) {
      console.log('🛡️ Discarded recording from a previous question (recorded for '
        + recordingQuestionIdRef.current + ', now on ' + questionId + ')');
      processedBlobRef.current = audioBlob;
      resetRecording();
      return;
    }

    if (isUploading || isTranscribing || hasUploadError) return;

    // Claim this recording synchronously, before any await or re-render, so a
    // second pass through this effect cannot schedule a duplicate upload.
    processedBlobRef.current = audioBlob;
    isProcessingUpload.current = true;

    // handleUploadAndTranscribe closes over this render's questionId, which the
    // guard above has already confirmed matches the recording.
    const timer = setTimeout(() => {
      handleUploadAndTranscribe();
    }, 100);

    return () => clearTimeout(timer);
  }, [audioBlob, questionId, isUploading, isTranscribing, hasUploadError]);

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handlePlayPause = async () => {
    if (audioRef.current) {
      try {
        if (isPlaying) {
          audioRef.current.pause();
          setIsPlaying(false);
        } else {
          await audioRef.current.play();
          setIsPlaying(true);
        }
      } catch (error) {
        console.error('Error playing audio:', error);
        alert(`Failed to play audio: ${error.message}`);
      }
    } else {
      console.error('Audio element not found');
      alert('Audio player not ready. Please try again.');
    }
  };

  // Wrapper functions to reset error state and handle async operations
  const handleStartRecording = async () => {
    setHasUploadError(false); // Clear error flag
    isProcessingUpload.current = false; // Clear processing flag
    // Bind this recording to the question on screen right now. The upload effect
    // refuses to submit it against any other question.
    recordingQuestionIdRef.current = questionId;
    processedBlobRef.current = null;
    await startRecording();
  };

  const handleResetRecording = async () => {
    isProcessingUpload.current = false; // Clear processing flag
    setShowStepTwo(false); // Reset stepped interface to Step 1
    setHasClickedContinue(false); // Reset continue state
    await resetRecording();
  };

  const handleReRecord = async () => {
    // Show confirmation dialog before clearing
    const confirmed = window.confirm(
      '⚠️ Are you sure you want to re-record?\n\n' +
      'This will remove your current recording and transcription from view. ' +
      'Your previous answer will still be saved in the system.\n\n' +
      'Click OK to proceed with re-recording, or Cancel to keep your current answer.'
    );
    
    if (!confirmed) {
      return; // User cancelled, don't proceed
    }
    
    // Clear all states for a fresh start
    isProcessingUpload.current = false; // Clear processing flag
    setTranscription('');
    setCleanedTranscription('');
    setFinalAnswer('');
    setAudioUrl(null);
    setShowThread(false);
    setFollowupQuestion('');
    setIsRecordingFollowup(false);
    setJustCompletedAnswer(false);
    
    // Reset the audio recorder
    await resetRecording();
    
    // console.log('🔄 Re-recording: All states cleared for fresh start');
  };

  const handleStopRecording = async () => {
    await stopRecording();
  };

  const handleUploadAndTranscribe = async () => {
    // Guard against duplicate processing (should already be set by effect)
    if (!audioBlob) {
      console.log('Skipping upload: no audioBlob');
      return;
    }
    
    // Double-check the flag (defensive programming)
    if (isProcessingUpload.current !== true) {
      console.warn('Upload called without flag set - this should not happen!');
      isProcessingUpload.current = true;
    }
    
    // FRONTEND VALIDATION: Check blob size before uploading
    // Backend rejects < 8KB (8192 bytes), so check here first
    const MIN_SIZE_BYTES = 8192; // 8KB - matches backend validation
    
    if (audioBlob.size < MIN_SIZE_BYTES) {
      console.warn('Recording too small:', audioBlob.size, 'bytes (minimum:', MIN_SIZE_BYTES, ')');
      
      // Show error immediately without uploading
      const errorMessage = '⏱️ Recording Too Short\n\n' +
        'Your recording is too short (less than 2-3 seconds).\n\n' +
        'Tips:\n' +
        '• Record for at least 2-3 seconds\n' +
        '• Speak a full sentence before stopping\n' +
        '• Don\'t stop immediately after starting\n\n' +
        '✅ Ready to try again - just press the red button.';
      
      // Set error flag to prevent retrigger - DON'T clear blob (might clear successful recordings)
      setHasUploadError(true);
      isProcessingUpload.current = false;
      
      // Now show error
      alert(errorMessage);
      
      return;
    }

    setIsUploading(true);
    try {
      // Demo mode: Use special demo upload function
      if (demoMode && demoSessionId) {
        setIsTranscribing(true);
        setIsUploading(false);

        // Convert blob to base64
        const reader = new FileReader();
        const base64Promise = new Promise((resolve) => {
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(audioBlob);
        });
        const audioData = await base64Promise;

        const demoResult = await fn.uploadDemoAudio({
          sessionId: demoSessionId,
          audioData: audioData
        });

        setTranscription(demoResult.data.transcription);
        setFinalAnswer(demoResult.data.transcription);
        setIsTranscribing(false);
        setJustCompletedAnswer(true);

        // Show warning about demo limits
        if (demoResult.data.remainingRecordings <= 3) {
          console.log(`⚠️ ${demoResult.data.remainingRecordings} recordings remaining in demo`);
        }

        // Reset for next recording
        handleResetRecording();
        setAudioUrl(null);

        if (onAnswerComplete) {
          onAnswerComplete({
            transcription: demoResult.data.transcription,
            isFollowup: false,
            demoMode: true
          });
        }
        return;
      }

      // Normal mode: Upload to Firebase Storage
      const timestamp = Date.now();
      
      // Determine correct extension based on blob type
      let extension = 'webm';
      if (audioBlob.type.includes('mp4')) {
        extension = 'mp4';
      } else if (audioBlob.type.includes('aac')) {
        extension = 'aac';
      } else if (audioBlob.type.includes('wav')) {
        extension = 'wav';
      }
      
      const fileName = `audio_${timestamp}.${extension}`;
      const storageRef = ref(storage, `stories/${storyId}/audio/${fileName}`);
      
      await uploadBytes(storageRef, audioBlob);
      const downloadURL = await getDownloadURL(storageRef);

      setIsTranscribing(true);
      setIsUploading(false);

      // Enhanced transcribe audio with parent answer support
      console.log('🎯 Transcribe audio parameters:', {
        storyId,
        questionId,
        audioUrl: downloadURL,
        parentAnswerId: parentAnswerId,
        isFollowup: isFollowup,
        followupQuestion: isFollowup ? question.text : null,
        questionObject: question
      });
      
      console.log('🔍 SPECIFIC followupQuestion value:', isFollowup ? question.text : null);
      console.log('🔍 question.text value:', question.text);
      console.log('🔍 isFollowup value:', isFollowup);
      console.log('🔍 parentAnswerId value:', parentAnswerId);
      console.log('🔍 existingAnswers array:', existingAnswers);
      console.log('🔍 calculated parentAnswerId would be:', existingAnswers.length > 0 ? existingAnswers[existingAnswers.length - 1].id : null);
      
      const transcribeResult = await fn.transcribeAudio({
        storyId,
        questionId,
        audioUrl: downloadURL,
        parentAnswerId: parentAnswerId,
        isFollowup: isFollowup,
        followupQuestion: isFollowup ? question.text : null,
        // NEW: Pass collaborative parameters
        isCollaborative: isCollaborative,
        authorId: authorId || currentUser.uid,
        authorEmail: authorEmail || currentUser.email
      });

      console.log('✅ Transcription completed:', transcribeResult.data.transcription);
      console.log('✅ Full transcription result:', transcribeResult.data);
      setTranscription(transcribeResult.data.transcription);
      setFinalAnswer(transcribeResult.data.transcription); // Set final answer immediately
      setIsTranscribing(false);
      setJustCompletedAnswer(true); // Flag that we just completed an answer
      
      console.log('🎯 Setting transcription state:', {
        transcription: transcribeResult.data.transcription,
        finalAnswer: transcribeResult.data.transcription,
        justCompletedAnswer: true
      });
      
      // Don't automatically clean - let user choose
      // Show answer thread after successful answer
      setShowThread(true);
      
      // Trigger AnswerThread refresh to show new answer
      setThreadRefreshTrigger(prev => prev + 1);

      // For stepped interface, automatically call onAnswerComplete when transcription is ready
      if (useSteppedInterface && onAnswerComplete) {
        onAnswerComplete({
          transcription: transcribeResult.data.transcription,
          isFollowup: isFollowup,
          audioUrl: downloadURL
        });
      }

      // DON'T reset recording state on success - keep transcription visible
      // Just clear the processing flag
      isProcessingUpload.current = false;
      setHasUploadError(false); // Clear any error flag

      // Notify parent component of completion with delay to ensure state is set
      // This reduces race condition risk
      setTimeout(() => {
        if (onAnswerComplete) {
          onAnswerComplete({
            answerId: transcribeResult.data.answerId,
            transcription: transcribeResult.data.transcription,
            isFollowup: isFollowup
          });
        }
      }, 100);

    } catch (error) {
      console.error('Error uploading and transcribing:', error);
      
      // Build helpful error message with actionable advice
      let errorMessage = 'Recording failed. Please try again.';
      let advice = '';
      
      if (error?.message) {
        // Handle specific Firebase function errors
        if (error.message.includes('microphone')) {
          errorMessage = '🎤 Microphone Issue\n\n' + error.message;
          advice = '\n\nTips:\n• Check microphone permissions in your browser\n• Ensure no other app is using the microphone';
        } else if (error.message.includes('no speech detected') || error.message.includes('empty') || error.message.includes('too quiet')) {
          errorMessage = '🎤 No Speech Detected\n\n' + error.message;
          advice = '\n\nTips:\n• Speak closer to the microphone\n• Speak louder and more clearly\n• Keep your hand away from the microphone\n• Check that you\'re not on mute\n• Try in a quieter environment';
        } else if (error.message.includes('too short')) {
          errorMessage = '⏱️ Recording Too Short\n\n' + error.message;
          advice = '\n\nTips:\n• Record for at least 2-3 seconds\n• Speak a full sentence before stopping';
        } else if (error.message.includes('unclear')) {
          errorMessage = '🔊 Audio Quality Issue\n\n' + error.message;
          advice = '\n\nTips:\n• Move to a quieter location\n• Speak more clearly\n• Keep your hand away from the microphone\n• Ensure microphone is not covered';
        } else if (error.code === 'functions/invalid-argument' || error.code === 'invalid-argument') {
          errorMessage = '⚠️ ' + error.message;
          advice = '\n\nPlease check your recording and try again.';
        } else {
          errorMessage = '❌ Recording Error\n\n' + error.message;
          advice = '\n\nPlease try recording again.';
        }
      }
      
      // Set error flag to prevent retrigger - DON'T clear blob (might clear successful recordings)
      setHasUploadError(true);
      isProcessingUpload.current = false;
      setIsUploading(false);
      setIsTranscribing(false);
      
      // Now show error
      alert(errorMessage + advice + '\n\n✅ Ready to record again - just press the red button.');
    }
  };

  const handleCleanTranscription = async () => {
    if (!transcription) return;

    setIsCleaning(true);
    try {
      // Get fresh existingAnswers to avoid stale closure issues
      const currentAnswers = existingAnswers;
      const latestAnswer = currentAnswers[currentAnswers.length - 1];
      
      if (!latestAnswer || !latestAnswer.id) {
        alert('Unable to clean transcription - answer not found. Please try recording again.');
        setIsCleaning(false);
        return;
      }

      const cleanupResult = await fn.cleanupTranscription({
        storyId,
        questionId,
        answerId: latestAnswer.id,
        rawTranscription: transcription
      });

      if (cleanupResult.data && cleanupResult.data.cleanedTranscription) {
        setCleanedTranscription(cleanupResult.data.cleanedTranscription);
      } else {
        alert('Failed to clean transcription - no cleaned text returned.');
      }
    } catch (error) {
      console.error('Error cleaning transcription:', error);
      alert('Failed to clean transcription. Please try again.');
    } finally {
      setIsCleaning(false);
    }
  };

  const handleSelectFinalAnswer = async (selectedText, isCleanedVersion = false) => {
    setFinalAnswer(selectedText);
    
    // Update the answer in the database to reflect the user's choice
    try {
      const latestAnswer = existingAnswers[existingAnswers.length - 1];
      if (latestAnswer) {
        // You would typically update the database here to mark which version was selected
        // For now, we'll just store it locally
        console.log(`User selected ${isCleanedVersion ? 'cleaned' : 'original'} version:`, selectedText);
      }
    } catch (error) {
      console.error('Error updating answer selection:', error);
    }
    
    alert(`${isCleanedVersion ? 'Cleaned' : 'Original'} transcription selected as final answer!`);
  };

  const handleGenerateFollowup = async () => {
    // Check demo story follow-up limit
    if (story?.isDemo && (story?.demoFollowupsUsed || 0) >= 1) {
      alert('🎯 Demo stories are limited to one follow-up question.\n\nTo generate unlimited follow-ups and create your full story, please sign up for Memory Stream!');
      return;
    }
    
    const answerToUse = finalAnswer || transcription;
    if (!answerToUse) return;

    setIsGeneratingFollowup(true);
    try {
      const result = await fn.generateSimpleFollowupQuestion({
        transcript: answerToUse,
        originalQuestion: question.text
      });

      if (result.data.success && result.data.question) {
        setFollowupQuestion(result.data.question);
        
        // Increment demo follow-up counter
        if (story?.isDemo) {
          const db = await import('../config/firebase').then(m => m.db);
          const { doc, updateDoc } = await import('firebase/firestore');
          await updateDoc(doc(db, 'stories', storyId), {
            demoFollowupsUsed: (story?.demoFollowupsUsed || 0) + 1
          });
        }
      } else {
        alert('Failed to generate follow-up question. Please try again.');
      }
    } catch (error) {
      console.error('Error generating follow-up question:', error);
      alert('Failed to generate follow-up question. Please try again.');
    } finally {
      setIsGeneratingFollowup(false);
    }
  };

  const handlePhotoUpload = async (updatedPhotos) => {
    try {
      const latestAnswer = existingAnswers[existingAnswers.length - 1];
      if (!latestAnswer) {
        alert('Please record an answer first before adding photos.');
        return;
      }

      // Update local state with the new photos array
      setPhotos(updatedPhotos);

      // Update the answer in Firestore with the new photos
      const answerRef = doc(db, 'stories', storyId, 'questions', questionId, 'answers', latestAnswer.id);
      await updateDoc(answerRef, {
        photos: updatedPhotos
      });

      // Don't close modal automatically - let user close when done
      console.log('Photos updated successfully');
    } catch (error) {
      console.error('Error updating photos:', error);
      alert('Failed to update photos. Please try again.');
    }
  };

  const handleRemovePhoto = async (photoIndex) => {
    try {
      const latestAnswer = existingAnswers[existingAnswers.length - 1];
      if (!latestAnswer) return;

      const newPhotos = photos.filter((_, index) => index !== photoIndex);
      setPhotos(newPhotos);

      // Update the answer in Firestore
      const answerRef = doc(db, 'stories', storyId, 'questions', questionId, 'answers', latestAnswer.id);
      await updateDoc(answerRef, {
        photos: newPhotos
      });

      console.log('Photo removed successfully');
    } catch (error) {
      console.error('Error removing photo:', error);
      alert('Failed to remove photo. Please try again.');
    }
  };

  const handleMovePhotoUp = async (photoIndex) => {
    if (photoIndex === 0) return; // Can't move first photo up
    
    try {
      const newPhotos = [...photos];
      // Swap with previous photo
      [newPhotos[photoIndex - 1], newPhotos[photoIndex]] = [newPhotos[photoIndex], newPhotos[photoIndex - 1]];
      setPhotos(newPhotos);

      // Update the answer in Firestore
      const latestAnswer = existingAnswers[existingAnswers.length - 1];
      if (latestAnswer) {
        const answerRef = doc(db, 'stories', storyId, 'questions', questionId, 'answers', latestAnswer.id);
        await updateDoc(answerRef, {
          photos: newPhotos
        });
      }
    } catch (error) {
      console.error('Error moving photo up:', error);
      alert('Failed to reorder photo. Please try again.');
    }
  };

  const handleMovePhotoDown = async (photoIndex) => {
    if (photoIndex === photos.length - 1) return; // Can't move last photo down
    
    try {
      const newPhotos = [...photos];
      // Swap with next photo
      [newPhotos[photoIndex], newPhotos[photoIndex + 1]] = [newPhotos[photoIndex + 1], newPhotos[photoIndex]];
      setPhotos(newPhotos);

      // Update the answer in Firestore
      const latestAnswer = existingAnswers[existingAnswers.length - 1];
      if (latestAnswer) {
        const answerRef = doc(db, 'stories', storyId, 'questions', questionId, 'answers', latestAnswer.id);
        await updateDoc(answerRef, {
          photos: newPhotos
        });
      }
    } catch (error) {
      console.error('Error moving photo down:', error);
      alert('Failed to reorder photo. Please try again.');
    }
  };

  const handleConvertToStoryQuestion = async () => {
    if (!followupQuestion || !storyId || !currentUser) return;

    try {
      // First, get all existing questions to determine the insertion point
      const questionsRef = collection(db, 'stories', storyId, 'questions');
      const questionsQuery = query(questionsRef, orderBy('order'));
      const questionsSnapshot = await getDocs(questionsQuery);
      
      const questions = questionsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Find the current question to insert after it
      const currentQuestionIndex = questions.findIndex(q => q.id === questionId);
      
      if (currentQuestionIndex === -1) {
        throw new Error('Current question not found');
      }

      // Use a transaction to ensure atomicity
      await runTransaction(db, async (transaction) => {
        // Create the new question with order positioned after current question
        const newQuestionId = uuidv4();
        const newQuestionRef = doc(db, 'stories', storyId, 'questions', newQuestionId);
        
        const insertAfterOrder = questions[currentQuestionIndex].order;
        
        // Rebalance orders: shift all questions after the current one by 1
        const questionsToUpdate = questions.slice(currentQuestionIndex + 1);
        questionsToUpdate.forEach(question => {
          const questionRef = doc(db, 'stories', storyId, 'questions', question.id);
          transaction.update(questionRef, {
            order: question.order + 1
          });
        });
        
        // Insert the new question right after the current one
        transaction.set(newQuestionRef, {
          id: newQuestionId,
          text: followupQuestion,
          order: insertAfterOrder + 1,
          storyId: storyId,
          userId: currentUser.uid,
          createdAt: serverTimestamp(),
          hasAnswer: false
        });
      });

      // Clear the follow-up question and show success message
      setFollowupQuestion('');
      setHasConvertedFollowup(true); // Mark that user has converted a follow-up for this answer
      alert('Follow-up question converted to a story question successfully!');
      
    } catch (error) {
      console.error('Error converting follow-up to story question:', error);
      alert('Failed to convert follow-up question. Please try again.');
    }
  };

  const handleStartFollowupRecording = () => {
    setIsRecordingFollowup(true);
  };

  const handleFollowupComplete = async ({ answerId, transcription, isFollowup: completedFollowup }) => {
    try {
      // Refresh answers to show the new follow-up
      if (onAnswerComplete) {
        await onAnswerComplete({ 
          answerId, 
          transcription, 
          questionId, 
          storyId,
          isFollowup: completedFollowup 
        });
      }
      
      setIsRecordingFollowup(false);
      setFollowupQuestion('');
      setShowThread(true);
      
      // Trigger AnswerThread refresh to show new follow-up
      setThreadRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Error handling follow-up completion:', error);
      // Still reset states even if callback fails
      setIsRecordingFollowup(false);
      setFollowupQuestion('');
    }
  };

  // Stepped Interface Render Functions
  const renderSteppedProgressIndicator = () => {
    const currentStep = getCurrentStep();
    const steps = ['Ready', 'Record', 'Recording', 'Process', 'Complete'];
    
    return (
      <div className="flex items-center justify-center space-x-1 md:space-x-2 mb-6 md:mb-8">
        {steps.map((stepName, index) => {
          const stepNumber = index + 1;
          return (
            <div key={stepNumber} className="flex items-center">
              <div className={`w-6 h-6 md:w-8 md:h-8 rounded-full flex items-center justify-center text-xs md:text-sm font-medium transition-colors ${
                stepNumber < currentStep 
                  ? 'bg-green-500 text-white' 
                  : stepNumber === currentStep 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-gray-200 text-gray-600'
              }`}>
                {stepNumber < currentStep ? '✓' : stepNumber}
              </div>
              {index < steps.length - 1 && (
                <div className={`w-4 md:w-8 h-1 mx-1 md:mx-2 transition-colors ${
                  stepNumber < currentStep ? 'bg-green-500' : 'bg-gray-200'
                }`} />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderSteppedInterface = () => {
    const currentStep = getCurrentStep();
    
    switch (currentStep) {
      case 1:
        return (
          <div className="text-center space-y-6">
            <div className="space-y-4">
              <h2 className="text-2xl font-semibold text-gray-900">Ready to Answer?</h2>
              <div className="text-gray-600 space-y-2">
                <p>Take a moment to think about your answer. Make some notes if you need to.</p>
                <p>When you're ready, we'll help you record your response step by step.</p>
              </div>
            </div>
            <button
              onClick={() => setShowStepTwo(true)}
              className="memory-stream-button text-lg px-8 py-4"
              disabled={isUploading || isTranscribing}
            >
              I'm Ready to Record
            </button>
          </div>
        );

      case 2:
        return (
          <div className="text-center space-y-6">
            <div className="space-y-4">
              <h2 className="text-2xl font-semibold text-gray-900">Ready to Record</h2>
              
              <div className="text-gray-600 space-y-2">
                <p>Great! Now press the red button below to start recording your answer.</p>
                <p>Speak clearly and take your time - you can always re-record if needed.</p>
              </div>
            </div>

            <button
              onClick={handleStartRecording}
              className="flex items-center justify-center w-24 h-24 bg-red-500 hover:bg-red-600 text-white rounded-full transition-all duration-200 mx-auto shadow-lg hover:shadow-xl"
            >
              <MicrophoneIcon className="h-12 w-12" />
            </button>
          </div>
        );

      case 3:
        return (
          <div className="text-center space-y-6">
            <div className="space-y-4">
              <h2 className="text-2xl font-semibold text-gray-900">
                {isPaused ? 'Recording Paused' : 'Recording Your Answer'}
              </h2>
              
              <div className={`${isPaused ? 'bg-yellow-50 border-yellow-200' : 'bg-red-50 border-red-200'} border rounded-lg p-4`}>
                <div className={`flex items-center justify-center space-x-2 ${isPaused ? 'text-yellow-800' : 'text-red-800'} mb-2`}>
                  <div className={`w-3 h-3 ${isPaused ? 'bg-yellow-500' : 'bg-red-500 rounded-full animate-pulse'}`} />
                  <span className="font-medium">
                    {isPaused ? 'Recording Paused' : 'Recording in Progress'}
                  </span>
                </div>
                <p className={`text-sm ${isPaused ? 'text-yellow-700' : 'text-red-700'}`}>
                  {isPaused ? 'Press play to resume when you are ready' : "Speak naturally - you're doing great!"}
                </p>
              </div>
              
              <div className="text-3xl font-mono text-gray-900">
                {Math.floor(duration / 60)}:{(duration % 60).toString().padStart(2, '0')}
              </div>

              {wakeLockSupported && !isPaused && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-sm text-green-700">
                    ✅ Your screen will stay awake during recording
                  </p>
                </div>
              )}
            </div>

            <div className="flex items-center justify-center space-x-6">
              {/* Pause/Resume Button */}
              <button
                onClick={isPaused ? resumeRecording : pauseRecording}
                className={`flex items-center justify-center w-20 h-20 rounded-full transition-all duration-200 shadow-lg hover:shadow-xl ${
                  isPaused 
                    ? 'bg-green-500 hover:bg-green-600 text-white' 
                    : 'bg-yellow-500 hover:bg-yellow-600 text-white'
                }`}
                title={isPaused ? "Resume Recording" : "Pause Recording"}
              >
                {isPaused ? (
                  <PlayIcon className="h-10 w-10" />
                ) : (
                  <PauseIcon className="h-10 w-10" />
                )}
              </button>

              {/* Stop Button */}
              <button
                onClick={handleStopRecording}
                className="flex items-center justify-center w-24 h-24 bg-gray-800 hover:bg-gray-900 text-white rounded-full transition-all duration-200 shadow-lg hover:shadow-xl"
                title="Stop Recording"
              >
                <StopIcon className="h-12 w-12" />
              </button>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="text-center space-y-6">
            <div className="space-y-4">
              <h2 className="text-2xl font-semibold text-gray-900">Processing Your Answer</h2>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <div className="flex items-center justify-center space-x-3 mb-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="text-lg text-blue-800 font-medium">
                    {isUploading ? 'Uploading your recording...' : 'Converting speech to text...'}
                  </span>
                </div>
                <p className="text-blue-700 text-sm">
                  This usually takes just a few moments. Please don't close this window.
                </p>
              </div>

              {duration > 0 && (
                <div className="text-gray-600">
                  <p>Recorded: {Math.floor(duration / 60)}:{(duration % 60).toString().padStart(2, '0')}</p>
                </div>
              )}
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-semibold text-gray-900">Review Your Answer</h2>
            </div>

            {/* Audio Playback - positioned immediately after title */}
            {audioUrl && (
              <div className="flex items-center justify-center space-x-4 p-4 bg-gray-50 rounded-lg">
                <button
                  onClick={async () => {
                    if (audioRef.current) {
                      if (isPlaying) {
                        audioRef.current.pause();
                        setIsPlaying(false);
                      } else {
                        await audioRef.current.play();
                        setIsPlaying(true);
                      }
                    }
                  }}
                  className="flex items-center justify-center w-12 h-12 bg-blue-500 hover:bg-blue-600 text-white rounded-full transition-colors"
                >
                  {isPlaying ? <PauseIcon className="h-6 w-6" /> : <PlayIcon className="h-6 w-6" />}
                </button>
                <span className="text-gray-600">Listen to your recording</span>
                <audio
                  ref={audioRef}
                  src={audioUrl}
                  onEnded={() => setIsPlaying(false)}
                  onPause={() => setIsPlaying(false)}
                />
              </div>
            )}

            {/* Only show transcription sections during active recording (not for existing answers) */}
            {!showThread && (
              <>
                {/* Original Transcription with editing options */}
                {transcription && finalAnswer !== cleanedTranscription && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-gray-900">Your Answer:</h4>
                      {!cleanedTranscription && transcription && (
                        <button
                          onClick={handleCleanTranscription}
                          disabled={isCleaning}
                          className="memory-stream-button-secondary text-sm"
                        >
                          {isCleaning ? 'Cleaning...' : 'Clean Up'}
                        </button>
                      )}
                    </div>
                    <div className="relative bg-gray-50 p-4 rounded-lg border border-gray-200">
                      <p className="text-gray-700 whitespace-pre-wrap">{transcription}</p>
                      {finalAnswer === transcription && transcription && (
                        <div className="absolute top-2 right-2">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            ✓ Selected
                          </span>
                        </div>
                      )}
                      {finalAnswer !== transcription && transcription && (
                        <div className="mt-3 text-right">
                          <button
                            onClick={() => handleSelectFinalAnswer(transcription, false)}
                            className="text-sm text-blue-600 hover:text-blue-800"
                          >
                            Use this version
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Cleaned Transcription */}
                {cleanedTranscription && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-gray-900">
                        {finalAnswer === cleanedTranscription ? 'Your Answer:' : 'AI Cleaned Version:'}
                      </h4>
                      {finalAnswer === cleanedTranscription && (
                        <button
                          onClick={() => handleSelectFinalAnswer(transcription, false)}
                          className="text-sm text-blue-600 hover:text-blue-800"
                        >
                          Show original version
                        </button>
                      )}
                    </div>
                    <div className="relative bg-green-50 p-4 rounded-lg border border-green-200">
                      <p className="text-gray-700 whitespace-pre-wrap">{cleanedTranscription}</p>
                      {finalAnswer === cleanedTranscription && (
                        <div className="absolute top-2 right-2">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            ✓ Selected
                          </span>
                        </div>
                      )}
                      {finalAnswer !== cleanedTranscription && (
                        <div className="mt-3 text-right">
                          <button
                            onClick={() => handleSelectFinalAnswer(cleanedTranscription, true)}
                            className="text-sm text-blue-600 hover:text-blue-800"
                          >
                            Use this version
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Show the answer thread with full functionality */}
            <div className="mt-6">
              <AnswerThread 
                storyId={storyId} 
                questionId={questionId} 
                question={question}
                refreshTrigger={threadRefreshTrigger}
                onReRecord={handleReRecord}
                userAccessLevel={userAccessLevel}
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
        {error}
      </div>
    );
  }

  // If user is a viewer, show read-only view with existing answers
  if (userAccessLevel === 'viewer') {
    return (
      <div className="space-y-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
          <p className="text-blue-800">
            👁️ <strong>Viewer Access:</strong> You can listen to recorded answers but cannot record new ones.
          </p>
        </div>
        
        {/* Show existing answers in read-only mode */}
        <div className="mt-6">
          <AnswerThread 
            storyId={storyId} 
            questionId={questionId} 
            question={question}
            refreshTrigger={threadRefreshTrigger}
            onReRecord={() => {}} // Disabled for viewers
            userAccessLevel={userAccessLevel}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {useSteppedInterface ? (
        // Stepped Interface for Senior-Friendly UX
        <div className="w-full md:max-w-2xl md:mx-auto space-y-8">
          {renderSteppedProgressIndicator()}
          {renderSteppedInterface()}
        </div>
      ) : (
        // Original Interface (unchanged)
        <>
          {/* Follow-up indicator */}
          {isFollowup && (
            <div className="bg-blue-50 border border-blue-200 px-4 py-2 rounded-md">
              <p className="text-blue-800 text-sm">Recording follow-up answer</p>
        </div>
      )}

      {/* Recording Controls */}
      <div className="text-center space-y-4">
        <div className="flex items-center justify-center space-x-4">
          {/* Show red record button only when no recording exists and no transcription */}
          {!isRecording && !audioBlob && !transcription && !cleanedTranscription && (
            <button
              onClick={handleStartRecording}
              className="flex items-center justify-center w-16 h-16 bg-red-500 hover:bg-red-600 text-white rounded-full transition-colors duration-200"
              disabled={isUploading || isTranscribing}
            >
              <MicrophoneIcon className="h-8 w-8" />
            </button>
          )}

          {/* Show pause/resume and stop buttons while recording */}
          {isRecording && (
            <div className="flex items-center space-x-4">
              {/* Pause/Resume Button */}
              <button
                onClick={isPaused ? resumeRecording : pauseRecording}
                className={`flex items-center justify-center w-12 h-12 rounded-full transition-colors duration-200 ${
                  isPaused 
                    ? 'bg-green-500 hover:bg-green-600 text-white' 
                    : 'bg-yellow-500 hover:bg-yellow-600 text-white'
                }`}
                title={isPaused ? "Resume Recording" : "Pause Recording"}
              >
                {isPaused ? (
                  <PlayIcon className="h-6 w-6" />
                ) : (
                  <PauseIcon className="h-6 w-6" />
                )}
              </button>

              {/* Stop Button */}
              <button
                onClick={handleStopRecording}
                className="flex items-center justify-center w-16 h-16 bg-gray-800 hover:bg-gray-900 text-white rounded-full transition-colors duration-200"
                title="Stop Recording"
              >
                <StopIcon className="h-8 w-8" />
              </button>
            </div>
          )}

          {/* Show playback controls when audio exists and not uploading/transcribing */}
          {audioBlob && !isUploading && !isTranscribing && (
            <button
              onClick={handlePlayPause}
              className="flex items-center justify-center w-12 h-12 bg-blue-500 hover:bg-blue-600 text-white rounded-full transition-colors duration-200"
            >
              {isPlaying ? (
                <PauseIcon className="h-6 w-6" />
              ) : (
                <PlayIcon className="h-6 w-6" />
              )}
            </button>
          )}
        </div>

        {/* Clear Instructions for Users */}
        {!isRecording && !transcription && !cleanedTranscription && (
          <div className="text-gray-600">
            <p className="text-lg font-medium">Press the red button to start recording</p>
            <p className="text-sm">Speak clearly and take your time</p>
          </div>
        )}

        {!isRecording && (transcription || cleanedTranscription) && (
          <div className="text-green-600">
            <p className="text-lg font-medium">Answer recorded successfully!</p>
            <p className="text-sm">You can re-record if you'd like to make changes</p>
          </div>
        )}

        {isRecording && (
          <div className={`${isPaused ? 'text-yellow-600' : 'text-red-600'}`}>
            <p className="text-lg font-medium">
              {isPaused ? 'Recording Paused' : 'Recording in progress...'}
            </p>
            <p className="text-sm">
              {isPaused ? 'Press play to resume' : 'Press the stop button when finished'}
            </p>
            {wakeLockSupported && !isPaused && (
              <p className="text-xs text-gray-500 mt-1">Screen will stay awake during recording</p>
            )}
          </div>
        )}

        {(isUploading || isTranscribing) && (
          <div className="text-blue-600">
            <p className="text-lg font-medium">
              {isUploading ? 'Uploading...' : 'Transcribing your answer...'}
            </p>
            <p className="text-sm">Please wait while we process your recording</p>
          </div>
        )}
      </div>

      {/* Duration Display */}
      {(isRecording || audioBlob) && (
        <div className="text-center">
          <span className="text-lg font-mono text-gray-600">
            {formatDuration(duration)}
          </span>
        </div>
      )}

      {/* Audio Player */}
      {audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          onEnded={() => {
            console.log('🎵 Audio playback ended');
            setIsPlaying(false);
          }}
          onLoadedData={() => {
            console.log('🎵 Audio loaded successfully:', audioUrl);
          }}
          onError={(e) => {
            console.error('🎵 Audio loading error:', e);
          }}
          className="hidden"
        />
      )}

      {/* Transcription Results - Only show when recording/processing, hide when thread is shown */}
      {(transcription || finalAnswer) && !showThread && (
        <div className="space-y-6">
          {/* Original Transcription */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-gray-900">Transcription:</h4>
              {!cleanedTranscription && transcription && (
                <button
                  onClick={handleCleanTranscription}
                  disabled={isCleaning}
                  className="memory-stream-button-secondary text-sm"
                >
                  {isCleaning ? 'Cleaning...' : 'Clean Up'}
                </button>
              )}
            </div>
            <div className="relative bg-gray-50 p-4 rounded-lg border border-gray-200">
              <p className="text-gray-700 whitespace-pre-wrap">{transcription || finalAnswer}</p>
              {finalAnswer === transcription && transcription && (
                <div className="absolute top-2 right-2">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    ✓ Selected
                  </span>
                </div>
              )}
              {finalAnswer !== transcription && transcription && (
                <div className="mt-3 text-right">
                  <button
                    onClick={() => handleSelectFinalAnswer(transcription, false)}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    Use this version
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Cleaned Transcription */}
          {cleanedTranscription && (
            <div className="space-y-3">
              <h4 className="font-medium text-gray-900">Cleaned Version:</h4>
              <div className="relative bg-green-50 p-4 rounded-lg border border-green-200">
                <p className="text-gray-700 whitespace-pre-wrap">{cleanedTranscription}</p>
                {finalAnswer === cleanedTranscription && (
                  <div className="absolute top-2 right-2">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      ✓ Selected
                    </span>
                  </div>
                )}
                {finalAnswer !== cleanedTranscription && (
                  <div className="mt-3 text-right">
                    <button
                      onClick={() => handleSelectFinalAnswer(cleanedTranscription, true)}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      Use this version
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Photo Upload Modal */}
      {showPhotoModal && ReactDOM.createPortal(
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
          style={{
            zIndex: 999999,
            position: 'fixed',
            isolation: 'isolate',
            WebkitTransform: 'translateZ(0)'
          }}
          onClick={() => setShowPhotoModal(false)}
        >
          <div
            className="bg-white rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto"
            style={{ zIndex: 1000000 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Add Photo to Answer</h3>
                <button
                  onClick={() => setShowPhotoModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                >
                  ×
                </button>
              </div>

              <PhotoUpload
                storyId={storyId}
                questionId={questionId}
                answerId={existingAnswers.length > 0 ? existingAnswers[existingAnswers.length - 1].id : null}
                existingPhotos={photos}
                onPhotosUpdate={handlePhotoUpload}
              />
            </div>
            <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end">
              <button
                onClick={() => setShowPhotoModal(false)}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Loading States */}
      {(isUploading || isTranscribing) && (
        <div className="flex items-center justify-center space-x-2 text-blue-600">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
          <span>
            {isUploading ? 'Uploading audio...' : 'Transcribing and cleaning up...'}
          </span>
        </div>
      )}

      {/* Show answer thread after successful recording */}
      {showThread && (
        <div className="mt-6">
          <AnswerThread 
            storyId={storyId} 
            questionId={questionId} 
            question={question}
            refreshTrigger={threadRefreshTrigger}
            onReRecord={handleReRecord}
            userAccessLevel={userAccessLevel}
          />
        </div>
      )}

      {/* Follow-up Actions - Moved to bottom */}
      {finalAnswer && !isFollowup && !hasConvertedFollowup && (
        <div className="mt-6 pt-4 border-t border-gray-200">
          {!followupQuestion ? (
            <button
              onClick={handleGenerateFollowup}
              disabled={isGeneratingFollowup}
              className="memory-stream-button flex items-center w-full justify-center"
            >
              <ChatBubbleLeftRightIcon className="h-5 w-5 mr-2" />
              {isGeneratingFollowup ? 'Generating Follow-up...' : 'Generate Follow-up Question'}
            </button>
          ) : !isRecordingFollowup ? (
            <div className="space-y-3">
              <h5 className="font-medium text-gray-900">Follow-up Question:</h5>
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-blue-800 mb-3">{followupQuestion}</p>
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => setFollowupQuestion('')}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    Generate different question
                  </button>
                </div>
                <div className="mt-3 pt-3 border-t border-blue-200">
                  <button
                    onClick={handleConvertToStoryQuestion}
                    className="flex items-center text-sm text-green-700 hover:text-green-900 font-medium hover:bg-green-50 px-2 py-1 rounded transition-colors"
                    title="Add this follow-up question to your story as a new question"
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Make this a story question
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                <h5 className="font-medium text-blue-900 mb-2">Follow-up Question:</h5>
                <p className="text-blue-800">{followupQuestion}</p>
              </div>
              
              <div className="pl-4">
                <AudioRecorder
                  storyId={storyId}
                  questionId={questionId}
                  question={{ ...question, text: followupQuestion }}
                  parentAnswerId={existingAnswers.length > 0 ? existingAnswers[existingAnswers.length - 1].id : null}
                  isFollowup={true}
                  existingAnswers={[]}
                  onAnswerComplete={handleFollowupComplete}
                />
              </div>
              
              <button
                onClick={() => {
                  setIsRecordingFollowup(false);
                }}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                ← Cancel follow-up
              </button>
            </div>
          )}
        </div>
      )}

      {/* Message when follow-up generation is disabled after conversion */}
      {finalAnswer && !isFollowup && hasConvertedFollowup && (
        <div className="mt-6 pt-4 border-t border-gray-200">
          <div className="text-center text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
            ✅ Follow-up question added to your story! You can find it in your questions list.
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
};
